import { Icon } from '@iconify/react'
import { Computed } from '@legendapp/state/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLoaderData } from '@tanstack/react-router'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { fetchSiteDetails, hostAdminUpdateSite, updateSite } from '~features/sites/api'
import { SitesAddress, SitesContact, SitesPersonsInCharge } from '~features/sites/types'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  CardFooter,
  IconButton,
  Input,
  Label,
  PhoneInput,
  Separator,
  Textarea,
} from '~in-app/components'
import Dropdown from '~in-app/components/Dropdown'
import MultiSelectDropdown, { Option } from '~in-app/components/MultiSelectDropdown'
import { auth$ } from '~in-app/legend-state'
import { fetchCountryList, fetchDistributorDivisionList, fetchDistributorList, fetchDropDownIntegratorList } from '~in-app/shareable/index.api'
import { fetchProductList } from '~in-app/shareable/products/api'
import {
  addressTypes,
  contactTypes,
} from '~in-app/types'
import { sitesValidation } from '~in-app/validation/sites.validation'

export const Route = createFileRoute('/console/sites/edit/$id')({
  component: EditSitesPage,
  loader: async ({ params: { id }, abortController }) => {
    let countryList: Option[] = []
    let data: any
    const countryResponse = await fetchCountryList()
    countryList = countryResponse
      ? countryResponse?.list?.map((item: any) => ({
        value: item.id,
        label: item.name,
      }))
      : []
    const privileges = auth$.privileges.get()
    if (privileges?.has('0.3.1.2')) {
      const response = await fetchSiteDetails({ id })
      data = response[0]
    }
    return { countryList, data }
  },
  staticData: {
    privileges: ['0.3.1.2'],
  },
})

function EditSitesPage() {
  const router = useRouter()
  const privilegeValue = auth$.privileges.get()
  const queryClient = useQueryClient()
  const { countryList, data } = useLoaderData({ from: Route.id })
  const formOpts = formOptions({
    defaultValues: {
      name: '',
      id: '',
      systemId: '',
      address: {
        line1: '',
        line2: '',
        line3: '',
        countryId: '',
        type: '',
        postcode: '',
        state: '',
        city: '',
      } as SitesAddress,
      active: true,
      remarks: '',
      desp: '',
      integratorId: '',
      clientId: auth$.clientId.get() !== null ? auth$.clientId.get() : undefined,
      divisionId: '',
      contacts: [{ contactVal: '', type: '', contactLocalVal: '' }] as SitesContact[],
      personsInCharge: [{ contacts: [] as SitesContact[],
        firstName: '',
        lastName: '' }] as SitesPersonsInCharge[],
      poNumber: '',
      productIds: [] as Option[],
      serverType: '',
      version: 0,
      division: { id: '' },
    },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: sitesValidation,
    },
    onSubmit: async ({ value }) => {
      // Handle empty values for 'address', 'desp', and 'remarks

      try {
        submitSites(value)
      }
      catch (error) {
        console.error('Update Sites failed:', error)
        toast.error(`Error Submitting: ${error}`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 500,
        })
      }
    },
  })

  const formControl = useForm(formOpts)
  const [value, setValue] = useState(['item-1', 'item-2', 'item-3', 'item-4'])
  useEffect(() => {
    const validateFields = async () => {
      const fieldMeta = formControl.state.fieldMeta
      if (fieldMeta && formControl.state.errors.length > 0) {
        const fieldsToValidate = Object.keys(fieldMeta) as string[]
        const validationResults = await Promise.all(
          fieldsToValidate.map(async (field) => {
            const result = await formControl.validateField(field as any, 'change')
            return { field, result }
          }),
        )

        for (const { field, result: errors } of validationResults) {
          if (errors.length > 0) {
            const fieldElement = document.getElementById(field)
            fieldElement?.focus()
            // Scroll to the field element if it exists
            fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            break
          }
        }
      }
    }

    validateFields()
  }, [formControl.state.errors])

  useEffect(() => {
    if (
      data
    ) {
      // First set all values except division
      Object.entries(data).forEach(([key, val]) => {
        formControl.setFieldValue(key as any, val)
      })

      // Then set divisionId after division list is loaded
      // if (data.division?.id) {
      //   formControl.setFieldValue('divisionId', data.division.id)
      // }

      formControl.setFieldValue('division', data.division)
      formControl.setFieldValue('integratorId', data.integrator.id)
      formControl.setFieldValue('divisionId', data.division.id)
      formControl.setFieldValue('address', data.address)
      formControl.setFieldValue('productIds', data.products.map((product: any) => ({ value: product.id, label: product.name })))
    }
  }, [data])
  const { data: distributorList = [] } = useQuery<Option[]>({
    queryKey: ['edit-sites-distributor-listing'],
    queryFn: async ({ signal }) => {
      const clientId = auth$.clientId.get()
      if (clientId || !auth$.privileges.get()?.has('0.3.7.0')) {
        return []
      }

      const response = await fetchDistributorList()
      return response?.list?.map((item: any) => ({
        label: item.name,
        value: item.id,
      })) ?? []
    },
  })

  // const { data: data, isSuccess: isSuccessDetails } = useQuery({
  //   queryKey: ['edit-sites-details'],
  //   queryFn: async () => {
  //     const response = await fetchSiteDetails({ id })
  //     return response[0]
  //   },
  //   enabled: isSuccessDistributor,
  // })
  const { data: divisionList = [] } = useQuery<Option[]>({
    queryKey: ['edit-sites-division-listing'],
    queryFn: async ({ signal }) => {
      const clientId = formControl.getFieldValue('clientId') || auth$?.clientId?.get() || data?.clientId

      if (!clientId || !auth$.privileges.get()?.has('0.4.0.0')) {
        return []
      }
      const response = await fetchDistributorDivisionList({
        clientId: clientId,
      })

      const flattenDivisionsWithReduce = (
        divisions: any[],
      ): Option[] => {
        return divisions.reduce((acc: Option[], division: any) => {
          acc.push({
            value: division.id,
            label: division.name,
          })

          if (division.mychilds && division.mychilds.length > 0) {
            acc = acc.concat(
              flattenDivisionsWithReduce(division.mychilds),
            )
          }

          return acc
        }, [])
      }

      return flattenDivisionsWithReduce(response.userDivisions)
    },
  })
  const { data: integratorList } = useQuery<Option[]>({
    queryKey: ['edit-sites-integrator-listing'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const clientId = formControl.getFieldValue('clientId') || auth$?.clientId?.get() || data?.clientId
      if (!clientId || !auth$.privileges.get()?.has('0.3.0.0')) {
        return [] as Option[]
      }

      const response = await fetchDropDownIntegratorList({
        abortCtrl: signal,
        clientIds: clientId,
      })
      const integratorList = response.list.map((item: any) => ({
        label: item.name,
        value: item.id,
      }))

      return integratorList
    },
  })
  const { data: productList } = useQuery<Option[]>({
    queryKey: ['edit-sites-products-listing'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!auth$.privileges.get()?.has('0.3.8.0')) {
        return [] as Option[]
      }

      const response = await fetchProductList({
        abortCtrl: signal,
      })
      const productList = response.list.map((item: any) => ({
        label: item.name,
        value: item.id,
      }))

      return productList
    },
  })

  const saveSitesFunc = () => {
    const mutation = useMutation({
      mutationFn: updateSite,
      onSuccess: (response) => {
        toast.success(`Record Updated Successfully`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 8000,
        })

        router.navigate({ to: '/console/sites/list' })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const saveHostAdminSitesFunc = () => {
    const mutation = useMutation({
      mutationFn: hostAdminUpdateSite,
      onSuccess: (response) => {
        toast.success(`Record Updated Successfully`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 8000,
        })

        router.navigate({ to: '/console/sites/list' })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const saveSitesMutation = saveSitesFunc()
  const saveHostAdminSitesMutation = saveHostAdminSitesFunc()

  const submitSites = async (value: any) => {
    try {
      const submitValue = JSON.parse(JSON.stringify(value))
      if (submitValue.productIds && Array.isArray(submitValue.productIds)) {
        submitValue.productIds = submitValue.productIds.map((product: { value: string }) => product.value)
      }
      if (submitValue.address) {
        const address = submitValue.address
        const keys = Object.keys(address) as (keyof typeof address)[]
        keys.forEach((key) => {
          if (!address[key]) {
            delete address[key]
          }
        })
        if (Object.keys(address).length === 0) {
          delete submitValue.address
        }
      }
      Object.entries(submitValue).forEach(([key, value]) => {
        if (value === '') {
          delete submitValue[key]
        }
      })
      if (auth$.clientId.get()) {
        saveSitesMutation.mutate([submitValue])
      }
      else {
        saveHostAdminSitesMutation.mutate([submitValue])
      }
    }
    catch (error) {
      console.error(error)
      toast.error(`Submit Error: ${error}`, {
        position: 'top-center',
        hideProgressBar: false,
        autoClose: 500,
      })
    }
  }

  return (
    <div>
      <div className="p-2">
        <IconButton
          color="muted"
          onClick={() => router.history.back()}
        >
          <Icon icon="lucide:arrow-left" />
        </IconButton>

        <span className="mb-4 ml-4 text-2xl font-bold">Update Sites Page</span>
      </div>

      <Card className="mt-4">
        <div className="p-2">
          <form
            noValidate
            onSubmit={
              (e) => {
                e.preventDefault()
                setValue(['item-1', 'item-2', 'item-3', 'item-4'])
                formControl.handleSubmit()
              }
            }
          >
            <CardContent className="mt-4 grid gap-4">
              {/* Site Information */}
              <Accordion
                type="multiple"
                value={value}
                onValueChange={setValue}
              >
                <AccordionItem value="item-1">
                  <AccordionTrigger>
                    <h4 className="text-lg font-semibold">
                      Site Information
                    </h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="grid gap-2">
                        <formControl.Field
                          name="name"
                          validators={
                            {
                              onChange:
                            sitesValidation.shape.name,
                            }
                          }
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="name"
                                  >
                                    Name
                                  </Label>

                                  <Input
                                    id="name"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid gap-2">
                        <formControl.Field
                          name="id"
                          validators={
                            {
                              onChange:
                            sitesValidation.shape.id,
                            }
                          }
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="id"
                                  >
                                    Site ID
                                  </Label>

                                  <Input
                                    id="id"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value.trim())
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid gap-2">
                        <formControl.Field
                          name="systemId"
                          validators={
                            {
                              onChange:
                            sitesValidation.shape.systemId,
                            }
                          }
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="id"
                                  >
                                    System ID
                                  </Label>

                                  <Input
                                    id="systemId"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value.trim())
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Description Field */}
                      <div className="grid gap-2">
                        <formControl.Field name="desp">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="clientDesp"
                                  >
                                    Description
                                  </Label>

                                  <Textarea
                                    id="desp"
                                    placeholder="Type your Description here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {/* Remarks Field */}
                      <div className="grid gap-2">
                        <formControl.Field name="remarks">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="siteRemarks"
                                  >
                                    Remarks
                                  </Label>

                                  <Textarea
                                    id="remarks"
                                    placeholder="Type your Remarks here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      {
                        !auth$.clientId.get() && (
                          <div className="grid gap-2">
                            <formControl.Field name="clientId">
                              {
                                field => (
                                  <>
                                    <div className="relative">
                                      <Label
                                        className="text-xs"
                                        htmlFor="siteClientId"
                                      >
                                        Distributor
                                      </Label>

                                      {
                                        !privilegeValue?.has('0.3.7.0')
                                        && (
                                          <span className="ml-1 text-xs text-red-500">(No Privilege)</span>
                                        )
                                      }

                                      <Computed>
                                        {
                                          () => (
                                            <Dropdown
                                              id="clientId"
                                              label=""
                                              options={distributorList}
                                              value={field.state.value}
                                              onValueChange={
                                                (e) => {
                                                  if (e !== '') {
                                                    field.handleChange(e)
                                                    field.validate('change')
                                                    formControl.setFieldValue('divisionId', '')
                                                    formControl.setFieldValue('integratorId', '')
                                                    if (privilegeValue?.has('0.3.0.0')) {
                                                      queryClient.invalidateQueries({ queryKey: ['add-sites-integrator-listing'] })
                                                    }
                                                    if (privilegeValue?.has('0.4.0.0')) {
                                                      queryClient.invalidateQueries({ queryKey: ['add-sites-division-listing'] })
                                                    }
                                                  }
                                                }
                                              }
                                            />
                                          )
                                        }
                                      </Computed>

                                    </div>

                                    {
                                      field.state.meta.errors && (
                                        <em
                                          className="error-message"
                                          role="alert"
                                        >
                                          {field.state.meta.errors.join(', ')}
                                        </em>
                                      )
                                    }
                                  </>
                                )
                              }
                            </formControl.Field>
                          </div>
                        )
                      }

                      <div className="grid gap-2">
                        <formControl.Field name="divisionId">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="siteDivisionId"
                                  >
                                    Division
                                  </Label>

                                  {
                                    !privilegeValue?.has('0.4.0.0')
                                    && (
                                      <span className="ml-1 text-xs text-red-500">(No Privilege)</span>
                                    )
                                  }

                                  <Computed>
                                    {
                                      () => (
                                        <Dropdown
                                          id="divisionId"
                                          label=""
                                          options={divisionList}
                                          value={field.state.value}
                                          onValueChange={
                                            (e) => {
                                              if (e !== '') {
                                                field.handleChange(e)
                                                field.validate('change')
                                              }
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid gap-2">
                        <formControl.Field name="integratorId">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="siteIntegratorId"
                                  >
                                    Integrator
                                  </Label>

                                  {
                                    !privilegeValue?.has('0.3.0.0')
                                    && (
                                      <span className="ml-1 text-xs text-red-500">(No Privilege)</span>
                                    )
                                  }

                                  <Computed>
                                    {
                                      () => (
                                        <Dropdown
                                          id="integratorId"
                                          label=""
                                          options={integratorList ?? []}
                                          value={field.state.value}
                                          onValueChange={
                                            (e) => {
                                              if (e !== '') {
                                                field.handleChange(e)
                                                field.validate('change')
                                              }
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid gap-2">
                        <formControl.Field name="serverType">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="siteServerType"
                                  >
                                    Server Type
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <Dropdown
                                          id="serverType"
                                          label=""
                                          value={field.state.value}
                                          options={
                                            [{
                                              label: 'Premise',
                                              value: 'P',
                                            }, {
                                              label: 'Cloud',
                                              value: 'C',
                                            }]
                                          }
                                          onValueChange={
                                            (e) => {
                                              if (e !== '') {
                                                field.handleChange(e)
                                                field.validate('change')
                                              }
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid gap-2">
                        <formControl.Field name="poNumber">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="sitePoNumber"
                                  >
                                    PO Number
                                  </Label>

                                  <Input
                                    id="poNumber"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value),
                                        field.validate('change')
                                      }
                                    }
                                  />

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-1">
                      <div className="grid gap-2">
                        <formControl.Field name="productIds">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="siteServerType"
                                  >
                                    Products
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <MultiSelectDropdown
                                          id="productIds"
                                          options={productList ?? []}
                                          value={field.state.value}
                                          onChange={
                                            (e) => {
                                              field.handleChange(e)
                                              field.validate('change')
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>

                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Address</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <div className="grid">
                      <formControl.Field name="address.line1">
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  required
                                  className="text-xs"
                                  htmlFor="addressLine1"
                                >
                                  Address Line 1
                                </Label>

                                <Input
                                  id="address.line1"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value),
                                      field.validate('change')
                                    }
                                  }
                                />
                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid">
                      <formControl.Field name="address.line2">
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  className="text-xs"
                                  htmlFor="addressLine2"
                                >
                                  Address Line 2
                                </Label>

                                <Input
                                  id="address.line2"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value),
                                      field.validate('change')
                                    }
                                  }
                                />
                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid">
                      <formControl.Field name="address.line3">
                        {
                          field => (
                            <>
                              <div className="relative">
                                <Label
                                  className="text-xs"
                                  htmlFor="addressLine3"
                                >
                                  Address Line 3
                                </Label>

                                <Input
                                  id="address.line3"
                                  type="text"
                                  value={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={
                                    (e) => {
                                      field.handleChange(e.target.value),
                                      field.validate('change')
                                    }
                                  }
                                />
                              </div>

                              {
                                field.state.meta.errors && (
                                  <em
                                    className="error-message"
                                    role="alert"
                                  >
                                    {field.state.meta.errors.join(', ')}
                                  </em>
                                )
                              }
                            </>
                          )
                        }
                      </formControl.Field>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="grid">
                        <formControl.Field name="address.postcode">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressPostcode"
                                  >
                                    Postcode
                                  </Label>

                                  <Input
                                    id="address.postcode"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value),
                                        field.validate('change')
                                      }
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field name="address.city">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressCity"
                                  >
                                    City
                                  </Label>

                                  <Input
                                    id="address.city"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value),
                                        field.validate('change')
                                      }
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field name="address.state">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressState"
                                  >
                                    State
                                  </Label>

                                  <Input
                                    id="address.state"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      (e) => {
                                        field.handleChange(e.target.value),
                                        field.validate('change')
                                      }
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="grid">
                        <formControl.Field name="address.countryId">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressCountry"
                                  >
                                    Country
                                  </Label>

                                  <Computed>
                                    {
                                      () => (
                                        <Dropdown
                                          id="address.countryId"
                                          label=""
                                          options={countryList}
                                          value={field.state.value}
                                          onValueChange={
                                            (e) => {
                                              if (e !== '') {
                                                field.handleChange(e)
                                                field.validate('change')
                                              }
                                            }
                                          }
                                        />
                                      )
                                    }
                                  </Computed>
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      <div className="grid">
                        <formControl.Field name="address.type">
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="addressType"
                                  >
                                    Type
                                  </Label>

                                  <Dropdown
                                    id="address.type"
                                    label=""
                                    options={addressTypes}
                                    value={field.state.value}
                                    onValueChange={
                                      (e) => {
                                        if (e !== '') {
                                          field.handleChange(e)
                                          field.validate('change')
                                        }
                                      }
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Contact</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <formControl.Field
                      mode="array"
                      name="contacts"
                    >
                      {
                        (field) => {
                          return (
                            <div className="w-full">
                              {
                                field.state.value
                                && field.state.value.length > 0 && (
                                  <div className="my-2 grid grid-cols-2 gap-2">
                                    <div className="grid gap-2">
                                      <Label
                                        required
                                        className="text-xs"
                                      >
                                        Contact Type
                                      </Label>
                                    </div>

                                    <div className="grid gap-2">
                                      <Label
                                        required
                                        className="text-xs"
                                      >
                                        Contact
                                      </Label>
                                    </div>
                                  </div>
                                )
                              }

                              {
                                field.state.value
                                && field.state.value.map((_, j) => {
                                  return (
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="grid gap-2">
                                        <formControl.Field
                                          key={j}
                                          name={`contacts[${j}].type`}
                                        >
                                          {
                                            (subField) => {
                                              return (
                                                <div className="relative flex flex-row items-center self-center">
                                                  <span className="mr-2">{`${j + 1}.`}</span>

                                                  <div className="relative w-full">
                                                    <Dropdown
                                                      id={`contacts[${j}].type`}
                                                      label=""
                                                      options={contactTypes}
                                                      value={subField.state.value}
                                                      onValueChange={
                                                        (e) => {
                                                          if (e !== '') {
                                                            subField.handleChange(e)
                                                            subField.validate('change')
                                                            formControl.setFieldValue(`contacts[${j}].contactVal`, '')
                                                          }
                                                        }
                                                      }
                                                    />

                                                    {
                                                      subField.state.meta.errors && (
                                                        <em
                                                          className="error-message"
                                                          role="alert"
                                                        >
                                                          {
                                                            subField.state.meta.errors.join(
                                                              ', ',
                                                            )
                                                          }
                                                        </em>
                                                      )
                                                    }
                                                  </div>
                                                </div>
                                              )
                                            }
                                          }
                                        </formControl.Field>
                                      </div>

                                      <div className="grid gap-2">
                                        <formControl.Field
                                          key={j}
                                          name={`contacts[${j}].contactVal`}
                                        >
                                          {
                                            (subSubField) => {
                                              return (
                                                <>

                                                  <div className="relative w-full">
                                                    <div className="relative flex w-full flex-row items-center self-center">
                                                      {
                                                        field.state.value[j].type === 'MOBILE' || field.state.value[j].type === 'BUSINESS_PHONE'
                                                          ? (
                                                              <PhoneInput
                                                                international
                                                                className="mt-1 flex-1"
                                                                defaultCountry="MY"
                                                                id={`contacts[${j}].contactVal`}
                                                                value={subSubField.state.value}
                                                                onChange={
                                                                  (value: { fullNumber: string, localNumber: string }) => {
                                                                    subSubField.handleChange(value.fullNumber)
                                                                    formControl.setFieldValue(`contacts[${j}].contactLocalVal`, value.localNumber)

                                                                    // Validate using localNumber
                                                                    subSubField.validate('change')
                                                                  }

                                                                }
                                                              />
                                                            )
                                                          : (
                                                              <Input
                                                                className="mt-1"
                                                                disabled={field.state.value[j].type === ''}
                                                                id={`contacts[${j}].contactVal`}
                                                                type="text"
                                                                value={
                                                                  subSubField.state.value
                                                                }
                                                                onBlur={
                                                                  subSubField.handleBlur
                                                                }
                                                                onChange={
                                                                  (e) => {
                                                                    subSubField.handleChange(
                                                                      e.target.value,
                                                                    )
                                                                    subSubField.validate(
                                                                      'change',
                                                                    )
                                                                  }
                                                                }
                                                              />
                                                            )
                                                      }

                                                      <span className="ml-2">
                                                        <IconButton
                                                          className="rounded-full"
                                                          color="muted"
                                                          type="button"
                                                          variant="text"
                                                          onClick={
                                                            () =>
                                                              field.removeValue(j)
                                                          }
                                                        >
                                                          <Icon
                                                            color="red"
                                                            icon="lucide:trash-2"
                                                          />
                                                        </IconButton>
                                                      </span>

                                                    </div>

                                                    {
                                                      subSubField.state.meta
                                                        .errors && (
                                                        <em
                                                          className="error-message"
                                                          role="alert"
                                                        >
                                                          {
                                                            subSubField.state.meta.errors.join(
                                                              ', ',
                                                            )
                                                          }
                                                        </em>
                                                      )
                                                    }
                                                  </div>

                                                </>
                                              )
                                            }
                                          }
                                        </formControl.Field>
                                      </div>

                                    </div>
                                  )
                                })
                              }

                              <Button
                                className="my-2 rounded-md  border border-slate-400 bg-transparent  hover:opacity-100"
                                color="muted"
                                type="button" // Ensure this line is present to prevent triggering onSubmit
                                onClick={
                                  () =>
                                    field.pushValue({ type: '', contactVal: '', contactLocalVal: '' })
                                }
                              >
                                Add Contact
                              </Button>
                            </div>
                          )
                        }
                      }
                    </formControl.Field>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4">
                  <AccordionTrigger>
                    <h4 className="text-xl font-semibold">Person In Charge</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <formControl.Field
                      mode="array"
                      name="personsInCharge"
                    >
                      {
                        (field) => {
                          return (
                            <>
                              <Button
                                className="mt-2 rounded-md border border-slate-400  bg-transparent hover:opacity-100"
                                color="muted"
                                type="button" // Ensure this line is present to prevent triggering onSubmit
                                onClick={
                                  () =>
                                    field.pushValue({
                                      firstName: '',
                                      lastName: '',
                                      contacts: [],
                                    })
                                }
                              >
                                Add person
                              </Button>

                              <div className="mt-4">
                                {
                                  field.state.value
                                  && field.state.value.map((_, i) => {
                                    return (
                                      <>
                                        <div className="gap-2">
                                          <div className="flex items-center space-x-2">
                                            <div className="text-base font-semibold">
                                              {`${i + 1}.`}
                                            </div>

                                            <div className="flex-1">
                                              <span className="ml-2 mt-4 text-base font-semibold">
                                                PIC Details
                                              </span>
                                            </div>

                                            {
                                              field.state.value.length > 1 && (
                                                <div>
                                                  <IconButton
                                                    className="rounded-full"
                                                    color="muted"
                                                    type="button"
                                                    variant="text"
                                                    disabled={
                                                      field.state.value.length <= 1
                                                    }
                                                    onClick={
                                                      () =>
                                                        field.removeValue(i)
                                                    }
                                                  >
                                                    <Icon
                                                      color="red"
                                                      icon="lucide:trash-2"
                                                    />
                                                  </IconButton>
                                                </div>
                                              )
                                            }

                                          </div>

                                          <div className="ml-2 mt-2 w-full">
                                            <div className="grid grid-cols-2 gap-2">
                                              <div className="grid gap-2">
                                                <formControl.Field
                                                  key={i}
                                                  name={`personsInCharge[${i}].firstName`}
                                                >
                                                  {
                                                    (subField) => {
                                                      return (
                                                        <>
                                                          <div className="relative">
                                                            <Label
                                                              className="text-xs"
                                                              htmlFor={`personsInCharge[${i}].firstName`}
                                                            >
                                                              First Name
                                                            </Label>

                                                            <Input
                                                              id={`personsInCharge[${i}].firstName`}
                                                              type="text"
                                                              value={
                                                                subField.state.value
                                                              }
                                                              onBlur={
                                                                subField.handleBlur
                                                              }
                                                              onChange={
                                                                e =>
                                                                  subField.handleChange(
                                                                    e.target.value,
                                                                  )
                                                              }
                                                            />
                                                          </div>

                                                          {
                                                            subField.state.meta
                                                              .errors && (
                                                              <em
                                                                className="error-message"
                                                                role="alert"
                                                              >
                                                                {
                                                                  subField.state.meta.errors.join(
                                                                    ', ',
                                                                  )
                                                                }
                                                              </em>
                                                            )
                                                          }
                                                        </>
                                                      )
                                                    }
                                                  }
                                                </formControl.Field>
                                              </div>

                                              <div className="grid gap-2">
                                                <formControl.Field
                                                  key={i}
                                                  name={`personsInCharge[${i}].lastName`}
                                                >
                                                  {
                                                    (subField) => {
                                                      return (
                                                        <>
                                                          <div className="relative">
                                                            <Label
                                                              className="text-xs"
                                                              htmlFor={`personsInCharge[${i}].lastName`}
                                                            >
                                                              Last Name
                                                            </Label>

                                                            <Input
                                                              id={`personsInCharge[${i}].lastName`}
                                                              type="text"
                                                              value={
                                                                subField.state.value
                                                              }
                                                              onBlur={
                                                                subField.handleBlur
                                                              }
                                                              onChange={
                                                                e =>
                                                                  subField.handleChange(
                                                                    e.target.value,
                                                                  )
                                                              }
                                                            />
                                                          </div>

                                                          {
                                                            subField.state.meta
                                                              .errors && (
                                                              <em
                                                                className="error-message"
                                                                role="alert"
                                                              >
                                                                {
                                                                  subField.state.meta.errors.join(
                                                                    ', ',
                                                                  )
                                                                }
                                                              </em>
                                                            )
                                                          }
                                                        </>
                                                      )
                                                    }
                                                  }
                                                </formControl.Field>
                                              </div>
                                            </div>

                                            <span className="text-base font-semibold">
                                              PIC Contact
                                            </span>

                                            <formControl.Field
                                              mode="array"
                                              name={`personsInCharge[${i}].contacts`}
                                            >
                                              {
                                                (field) => {
                                                  return (
                                                    <div className="w-full">
                                                      {
                                                        field.state.value.length
                                                        > 0 && (
                                                          <div className="my-2 grid grid-cols-2 gap-2">
                                                            <div className="grid gap-2">
                                                              <Label
                                                                required
                                                                className="text-xs"
                                                              >
                                                                Contact Type
                                                              </Label>
                                                            </div>

                                                            <div className="grid gap-2">
                                                              <Label
                                                                required
                                                                className="text-xs"
                                                              >
                                                                Contact
                                                              </Label>
                                                            </div>
                                                          </div>
                                                        )
                                                      }

                                                      {
                                                        field.state.value
                                                        && field.state.value.map(
                                                          (_, j) => {
                                                            return (
                                                              <div className="grid grid-cols-2 gap-2">
                                                                <div className="grid gap-2">
                                                                  <formControl.Field
                                                                    key={i}
                                                                    name={`personsInCharge[${i}].contacts[${j}].type`}
                                                                  >
                                                                    {
                                                                      (
                                                                        subSubField,
                                                                      ) => {
                                                                        return (
                                                                          <>
                                                                            <div className="relative flex flex-row items-center self-center">
                                                                              <span className="mr-2">{`${j + 1}.`}</span>

                                                                              <div className="relative w-full">
                                                                                <Dropdown
                                                                                  id={`personsInCharge[${i}].contacts[${j}].type`}
                                                                                  label=""
                                                                                  options={
                                                                                    contactTypes
                                                                                  }
                                                                                  value={
                                                                                    subSubField
                                                                                      .state
                                                                                      .value
                                                                                  }
                                                                                  onValueChange={
                                                                                    (e) => {
                                                                                      if (e !== '') {
                                                                                        subSubField.handleChange(
                                                                                          e,
                                                                                        )
                                                                                        formControl.setFieldValue(`personsInCharge[${i}].contacts[${j}].contactVal`, '')
                                                                                        formControl.setFieldValue(`personsInCharge[${i}].contacts[${j}].contactLocalVal`, '')
                                                                                      }
                                                                                    }
                                                                                  }
                                                                                />

                                                                                {
                                                                                  subSubField
                                                                                    .state
                                                                                    .meta
                                                                                    .errors && (
                                                                                    <em
                                                                                      className="error-message"
                                                                                      role="alert"
                                                                                    >
                                                                                      {
                                                                                        subSubField.state.meta.errors.join(
                                                                                          ', ',
                                                                                        )
                                                                                      }
                                                                                    </em>
                                                                                  )
                                                                                }
                                                                              </div>
                                                                            </div>

                                                                          </>
                                                                        )
                                                                      }
                                                                    }
                                                                  </formControl.Field>
                                                                </div>

                                                                <div className="grid gap-2">
                                                                  <formControl.Field
                                                                    key={j}
                                                                    name={`personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                  >
                                                                    {
                                                                      (
                                                                        subSubField,
                                                                      ) => {
                                                                        return (
                                                                          <>

                                                                            <div className="relative w-full">
                                                                              <div className="relative flex w-full flex-row items-center self-center">
                                                                                {
                                                                                  field.state.value[j].type === 'MOBILE' || field.state.value[j].type === 'BUSINESS_PHONE'
                                                                                    ? (
                                                                                        <PhoneInput
                                                                                          international
                                                                                          className="mt-1 flex-1"
                                                                                          defaultCountry="MY"
                                                                                          id={`personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                                          value={subSubField.state.value}
                                                                                          onChange={
                                                                                            (value: { fullNumber: string, localNumber: string }) => {
                                                                                              subSubField.handleChange(value.fullNumber)
                                                                                              formControl.setFieldValue(`personsInCharge[${i}].contacts[${j}].contactLocalVal`, value.localNumber)

                                                                                              // Validate using localNumber
                                                                                              subSubField.validate('change')
                                                                                            }
                                                                                          }
                                                                                        />
                                                                                      )
                                                                                    : (
                                                                                        <Input
                                                                                          className="mt-1"
                                                                                          disabled={field.state.value[j].type === ''}
                                                                                          id={`personsInCharge[${i}].contacts[${j}].contactVal`}
                                                                                          type="text"
                                                                                          value={subSubField.state.value}
                                                                                          onBlur={subSubField.handleBlur}
                                                                                          onChange={e => subSubField.handleChange(e.target.value)}
                                                                                        />
                                                                                      )
                                                                                }

                                                                                <span className="ml-2">
                                                                                  <IconButton
                                                                                    className="rounded-full"
                                                                                    color="muted"
                                                                                    type="button"
                                                                                    variant="text"
                                                                                    onClick={
                                                                                      () =>
                                                                                        field.removeValue(
                                                                                          j,
                                                                                        )
                                                                                    }
                                                                                  >
                                                                                    <Icon
                                                                                      color="red"
                                                                                      icon="lucide:trash-2"
                                                                                    />
                                                                                  </IconButton>
                                                                                </span>
                                                                              </div>

                                                                              {
                                                                                subSubField
                                                                                  .state
                                                                                  .meta
                                                                                  .errors && (
                                                                                  <em
                                                                                    className="error-message"
                                                                                    role="alert"
                                                                                  >
                                                                                    {
                                                                                      subSubField.state.meta.errors.join(
                                                                                        ', ',
                                                                                      )
                                                                                    }
                                                                                  </em>
                                                                                )
                                                                              }
                                                                            </div>

                                                                          </>
                                                                        )
                                                                      }
                                                                    }
                                                                  </formControl.Field>
                                                                </div>

                                                              </div>
                                                            )
                                                          },
                                                        )
                                                      }

                                                      <Button
                                                        className="my-2 rounded-md  border border-slate-400 bg-transparent  hover:opacity-100"
                                                        color="muted"
                                                        type="button" // Ensure this line is present to prevent triggering onSubmit
                                                        onClick={
                                                          () =>
                                                            field.pushValue({
                                                              type: '',
                                                              contactVal: '',
                                                              contactLocalVal: '',
                                                            })
                                                        }
                                                      >
                                                        Add Contact
                                                      </Button>
                                                    </div>
                                                  )
                                                }
                                              }
                                            </formControl.Field>
                                          </div>
                                        </div>

                                        {
                                          field.state.value.length !== i + 1 && (
                                            <Separator className="mb-4 mt-2" />
                                          )
                                        }
                                      </>
                                    )
                                  })
                                }
                              </div>
                            </>
                          )
                        }
                      }
                    </formControl.Field>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>

            <CardFooter>
              <formControl.Subscribe
                selector={state => [state.canSubmit, state.isSubmitting]}
              >
                {
                  ([canSubmit, isSubmitting]) => (
                    <Button
                      fullWidth
                      disabled={!privilegeValue?.has('0.3.1.2')}
                    >
                      {isSubmitting ? '...' : 'Save'}
                    </Button>
                  )
                }
              </formControl.Subscribe>
            </CardFooter>
          </form>
        </div>
      </Card>
    </div>
  )
}
